import React, { useEffect, useState, useMemo } from 'react';
import CommissionerMasterGuide from './CommissionerMasterGuide';
import './CommissionerHQ.css';

const API = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────
// Inline color tokens — maps to CSS custom properties defined in App.css
// ─────────────────────────────────────────────
const HQ = {
  bg:         'var(--hq-bg)',
  panel:      'var(--hq-panel)',
  panelDeep:  'var(--hq-panel-deep)',
  border:     'var(--hq-border)',
  borderSoft: 'var(--glass-border)',
  text:       'var(--text)',
  text2:      'var(--text2)',
  text3:      'var(--text3)',
  amber:      'var(--hq-accent)',
  red:        'var(--red)',
  green:      'var(--green)',
  blue:       '#60a5fa',
  purple:     '#a855f7',
  warning:    '#fbbf24',
};

const SS_KEY = 'hq.pass.v1';

// ─────────────────────────────────────────────
// Password gate
// ─────────────────────────────────────────────
function PasswordGate({ onAuthenticated }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw) return;
    setBusy(true);
    setErr('');
    try {
      const r = await fetch(`${API}/api/hq/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (r.status === 503) {
        setErr('SYSTEM NOT CONFIGURED — set COMMISSIONER_PASSWORD on the server');
      } else if (r.status === 401) {
        setErr('ACCESS DENIED');
      } else if (!r.ok) {
        setErr(`SERVER ERROR (HTTP ${r.status})`);
      } else {
        sessionStorage.setItem(SS_KEY, pw);
        onAuthenticated(pw);
      }
    } catch (err) {
      const msg = err?.message || 'unknown';
      setErr(`CONNECTION FAILED — ${msg}`);
    }
    setBusy(false);
  };

  return (
    <div className="hq-gate-wrap">
      <form onSubmit={submit} className="hq-gate-card">
        <div className="hq-gate-stripe" />
        <div className="hq-gate-eyebrow">Restricted Access</div>
        <h1 className="hq-gate-title">COMMISSIONER HQ</h1>
        <div className="hq-gate-desc">Authorization required to continue.</div>
        <input
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Enter passphrase"
          className="hq-gate-input"
        />
        <button type="submit" disabled={busy || !pw} className="hq-gate-btn">
          {busy ? 'Verifying...' : 'Authorize'}
        </button>
        {err && <div className="hq-gate-error">⨯ {err}</div>}
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────
// UI primitives
// ─────────────────────────────────────────────
const Section = ({ children, title, sub }) => (
  <section className="hq-section">
    <div className="hq-section-header">
      <h2 className="hq-section-title">{title}</h2>
      {sub && <span className="hq-section-sub">{sub}</span>}
    </div>
    {children}
  </section>
);

const Panel = ({ children, accent, style }) => (
  <div style={{
    background: HQ.panel,
    border: `1px solid ${HQ.borderSoft}`,
    borderLeft: accent ? `3px solid ${accent}` : `1px solid ${HQ.borderSoft}`,
    borderRadius: 10,
    padding: 16,
    ...style,
  }}>
    {children}
  </div>
);

const Stat = ({ label, value, accent, big }) => (
  <div style={{
    background: HQ.panelDeep,
    border: `1px solid ${HQ.borderSoft}`,
    borderRadius: 8,
    padding: big ? '14px 16px' : '10px 12px',
    minWidth: big ? 140 : 100,
  }}>
    <div style={{
      fontFamily: 'var(--font-display)',
      fontSize: big ? '2rem' : '1.4rem',
      lineHeight: 1,
      color: accent || HQ.text,
    }}>{value}</div>
    <div style={{
      fontSize: 9,
      letterSpacing: '0.2em',
      color: HQ.text3,
      textTransform: 'uppercase',
      marginTop: 6,
      fontFamily: 'var(--font-mono)',
    }}>{label}</div>
  </div>
);

const Tag = ({ children, color }) => (
  <span style={{
    fontFamily: 'var(--font-mono)',
    fontSize: 9,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: 4,
    color: color || HQ.text2,
    background: `${color || HQ.text3}1a`,
    border: `1px solid ${color || HQ.text3}55`,
    whiteSpace: 'nowrap',
  }}>{children}</span>
);

// Narrative renderer that bolds **text**
function Narrative({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <div style={{ fontSize: 13, color: HQ.text2, lineHeight: 1.55 }}>
      {parts.map((p, i) => p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i} style={{ color: HQ.text }}>{p.slice(2, -2)}</strong>
      ) : <span key={i}>{p}</span>)}
    </div>
  );
}

// ─────────────────────────────────────────────
// SYSTEM 1 — League Health
// ─────────────────────────────────────────────
function System1({ data }) {
  if (!data) return null;
  const cb = data.competitive_balance || {};
  const components = data.components || {};
  const heatmap = data.engagement_heatmap || [];
  const cycle = data.dynasty_cycle_map || [];

  const statusColor = {
    active: HQ.green, dangerous: HQ.amber, steady: HQ.blue,
    fading: HQ.warning, disengaging: HQ.red,
  };
  const phaseColor = {
    contender: HQ.green, emerging: HQ.blue, drifting: HQ.text2,
    rebuilding: HQ.warning, collapsing: HQ.red, early: HQ.text3,
  };

  return (
    <>
      <Section title="League Vital Signs" sub={`Composite health · ${data.health_grade}`}>
        <Panel accent={data.health_score >= 70 ? HQ.green : data.health_score >= 50 ? HQ.amber : HQ.red}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <Stat label="Health Score" value={`${data.health_score}`} big accent={
              data.health_score >= 70 ? HQ.green : data.health_score >= 50 ? HQ.amber : HQ.red
            } />
            <Stat label="Status" value={data.health_grade} accent={HQ.amber} />
            <Stat label="Field" value={cb.label} accent={HQ.blue} />
          </div>
          <Narrative text={data.health_narrative} />
        </Panel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 14 }}>
          {Object.entries(components).map(([k, v]) => (
            <Stat key={k} label={k.replace(/_/g, ' ')} value={`${Math.round(v)}`} accent={
              v >= 70 ? HQ.green : v >= 50 ? HQ.amber : HQ.red
            } />
          ))}
        </div>
      </Section>

      <Section title="Engagement Heatmap" sub="Who is alive, fading, and gone">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {heatmap.map((h) => (
            <div key={h.user_id} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 90px 1fr',
              gap: 12,
              alignItems: 'center',
              padding: '10px 12px',
              background: HQ.panel,
              border: `1px solid ${HQ.borderSoft}`,
              borderLeft: `3px solid ${statusColor[h.status] || HQ.text3}`,
              borderRadius: 8,
            }}>
              <div style={{ fontWeight: 600, color: HQ.text, fontSize: 13 }}>
                {h.display_name}
                {h.is_orphan_risk && <span style={{ marginLeft: 8 }}><Tag color={HQ.red}>Orphan Risk</Tag></span>}
              </div>
              <Tag color={statusColor[h.status]}>{h.status}</Tag>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: HQ.text2, textAlign: 'right' }}>
                {h.activity_score.toFixed(1)} · z {h.z_score}
              </div>
              <div style={{ fontSize: 11, color: HQ.text3 }}>{h.label}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Dynasty Cycle Map" sub="Who's contending, who's collapsing">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {cycle.map((c) => (
            <Panel key={c.user_id} accent={phaseColor[c.phase] || HQ.text3}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: HQ.text, fontWeight: 600, fontSize: 13 }}>{c.display_name}</span>
                <Tag color={phaseColor[c.phase]}>{c.phase_label}</Tag>
              </div>
              <div style={{ fontSize: 11, color: HQ.text3, fontFamily: 'var(--font-mono)' }}>
                {c.record} · CHAMP {Math.round(c.championship_odds * 100)}% · PO {Math.round(c.playoff_odds * 100)}%
              </div>
              <div style={{ fontSize: 11, color: HQ.text2, marginTop: 4 }}>{c.archetype}</div>
            </Panel>
          ))}
        </div>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// SYSTEM 2 — Behavioral Intelligence
// ─────────────────────────────────────────────
function System2({ data }) {
  if (!data) return null;
  const dossiers = data.dossiers || [];
  const panic = data.panic_radar || [];
  const exploit = data.exploit_index || [];
  const trust = data.trust_ranking || [];
  const [open, setOpen] = useState(null);

  return (
    <>
      <Section title="Panic Radar" sub={`${panic.length} manager(s) showing stress patterns`}>
        {panic.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No panic signatures detected.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {panic.map((p) => (
              <Panel key={p.user_id} accent={p.panic_score >= 70 ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                  <strong style={{ color: HQ.text, fontSize: 14 }}>{p.display_name}</strong>
                  <Tag color={HQ.red}>Panic {p.panic_score}</Tag>
                </div>
                <Narrative text={p.narrative} />
                <ul style={{ marginTop: 8, paddingLeft: 18, color: HQ.text3, fontSize: 12, lineHeight: 1.7 }}>
                  {p.signals.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Exploit Index" sub="Pressure points worth knowing">
        {exploit.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No exploitable patterns surfaced.</div></Panel>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {exploit.map((e) => (
              <Panel key={e.user_id} accent={e.exploit_score >= 50 ? HQ.amber : HQ.text3}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong style={{ color: HQ.text, fontSize: 13 }}>{e.display_name}</strong>
                  <Tag color={HQ.amber}>{e.exploit_score}</Tag>
                </div>
                <ul style={{ paddingLeft: 16, fontSize: 12, color: HQ.text2, lineHeight: 1.6, margin: 0 }}>
                  {e.flags.map((f, i) => (
                    <li key={i}>
                      {f.flag} <span style={{ color: HQ.text3, fontSize: 10 }}>· {f.severity}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Manager Dossiers" sub="Tap a name for the deep file">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {dossiers.map((d) => (
            <div key={d.user_id}>
              <button
                onClick={() => setOpen(open === d.user_id ? null : d.user_id)}
                style={{
                  width: '100%', textAlign: 'left',
                  background: HQ.panel,
                  border: `1px solid ${HQ.borderSoft}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto auto',
                  gap: 10,
                  alignItems: 'center',
                  color: HQ.text,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 13 }}>{d.display_name}</span>
                <Tag color={HQ.blue}>{d.archetype}</Tag>
                <Tag color={d.volatility_label === 'Volatile' ? HQ.red : d.volatility_label === 'Reactive' ? HQ.amber : HQ.green}>
                  {d.volatility_label}
                </Tag>
                <span style={{ color: HQ.text3, fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                  {open === d.user_id ? '▾' : '▸'}
                </span>
              </button>
              {open === d.user_id && (
                <Panel style={{ marginTop: 4, marginBottom: 6 }} accent={HQ.amber}>
                  <Narrative text={d.summary} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 12 }}>
                    <Stat label="Trust" value={`${d.trust_score}`} accent={d.trust_score >= 70 ? HQ.green : d.trust_score >= 50 ? HQ.amber : HQ.red} />
                    <Stat label="Aggression" value={`${d.trade_aggression}`} accent={HQ.blue} />
                    <Stat label="Panic Likelihood" value={`${d.panic_likelihood}`} accent={d.panic_likelihood >= 50 ? HQ.red : HQ.green} />
                    <Stat label="Exploit Risk" value={`${d.exploit_susceptibility}`} accent={HQ.amber} />
                    <Stat label="Rivalry Heat" value={`${Math.round(d.rivalry_intensity)}`} accent={HQ.purple} />
                    <Stat label="Eras" value={`${d.eras_count}`} accent={HQ.text2} />
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: HQ.text3 }}>
                    Negotiation: <span style={{ color: HQ.text2 }}>{d.negotiation_style}</span> ·
                    Mindset: <span style={{ color: HQ.text2 }}> {d.rebuild_patience}</span>
                    {d.nemesis_name && <> · Nemesis: <span style={{ color: HQ.purple }}>{d.nemesis_name}</span></>}
                  </div>
                  {d.tags?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                      {d.tags.map((t) => <Tag key={t} color={HQ.amber}>{t}</Tag>)}
                    </div>
                  )}
                </Panel>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Trustworthiness Ranking" sub="Trade reliability from highest to most suspect">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {trust.map((t, i) => (
            <div key={t.user_id} style={{
              display: 'grid', gridTemplateColumns: '32px 1fr 80px',
              gap: 10, alignItems: 'center', padding: '8px 12px',
              background: HQ.panel, border: `1px solid ${HQ.borderSoft}`, borderRadius: 6,
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: HQ.text3 }}>#{i + 1}</span>
              <span style={{ color: HQ.text, fontSize: 13 }}>{t.display_name}</span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'right',
                color: t.trust_score >= 70 ? HQ.green : t.trust_score >= 50 ? HQ.amber : HQ.red,
              }}>{t.trust_score}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// SYSTEM 3 — Governance & Integrity
// ─────────────────────────────────────────────
function System3({ data }) {
  if (!data) return null;
  const collusion = data.collusion_flags || [];
  const tank = data.tank_risks || [];
  const stress = data.rule_stress || [];

  return (
    <>
      <Section title="Anomalous Behavior Detection" sub="Patterns worth a second look — not accusations">
        {collusion.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No anomalous trade patterns detected.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {collusion.map((c, i) => (
              <Panel key={i} accent={c.severity === 'high' ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                  <strong style={{ color: HQ.text, fontSize: 14 }}>
                    {c.user_a_name} ↔ {c.user_b_name}
                  </strong>
                  <Tag color={c.severity === 'high' ? HQ.red : HQ.amber}>{c.severity}</Tag>
                </div>
                <Narrative text={c.narrative} />
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  {c.flags.map((f, j) => <Tag key={j} color={HQ.amber}>{f}</Tag>)}
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Tank Risk" sub="Lineup negligence + asset liquidation">
        {tank.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No tank signals.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tank.map((t) => (
              <Panel key={t.user_id} accent={t.severity === 'high' ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                  <strong style={{ color: HQ.text, fontSize: 14 }}>{t.display_name}</strong>
                  <Tag color={HQ.text3}>{t.record}</Tag>
                  <Tag color={HQ.warning}>PO {Math.round(t.playoff_odds * 100)}%</Tag>
                </div>
                <Narrative text={t.narrative} />
                <ul style={{ marginTop: 8, paddingLeft: 18, color: HQ.text3, fontSize: 12, lineHeight: 1.6 }}>
                  {t.indicators.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Rule Stress Testing" sub="Statistical outliers in scoring + roster activity">
        {stress.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No outliers above threshold.</div></Panel>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {stress.map((s, i) => (
              <Panel key={i} accent={HQ.amber}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <strong style={{ color: HQ.text, fontSize: 13 }}>{s.display_name}</strong>
                  <Tag color={HQ.amber}>z {s.z_score}</Tag>
                </div>
                <div style={{ fontSize: 11, color: HQ.text3, marginBottom: 6 }}>{s.type}</div>
                <Narrative text={s.narrative} />
              </Panel>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// SYSTEM 4 — Narrative & Culture
// ─────────────────────────────────────────────
function System4({ data }) {
  if (!data) return null;
  const heat = data.rivalry_heat_index || [];
  const era = data.league_era || {};
  const reputations = data.franchise_reputations || [];

  const idColor = {
    villain: HQ.red, tactician: HQ.green, chaos: HQ.purple,
    prospect: HQ.blue, choker: HQ.amber, neutral: HQ.text2,
  };

  return (
    <>
      <Section title="The Current Era" sub={era.intensity}>
        <Panel accent={HQ.amber}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 28 }}>{era.icon || '⚡'}</span>
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: '1.6rem',
              letterSpacing: '3px', color: HQ.text, lineHeight: 1, textTransform: 'uppercase',
            }}>{era.name}</h3>
            <Tag color={HQ.amber}>{era.intensity}</Tag>
          </div>
          <Narrative text={era.narrative} />
          {era.all_archetype_counts && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {Object.entries(era.all_archetype_counts).map(([a, c]) => (
                <Tag key={a} color={a === era.dominant_archetype ? HQ.amber : HQ.text3}>
                  {a} · {c}
                </Tag>
              ))}
            </div>
          )}
        </Panel>
      </Section>

      {era.trajectory?.length > 0 && (
        <Section title="Era Trajectory" sub="Dominant archetype season by season">
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${era.trajectory.length}, 1fr)`, gap: 8 }}>
            {era.trajectory.map((t, i) => (
              <Panel key={t.season} accent={i === era.trajectory.length - 1 ? HQ.amber : HQ.text3}>
                <div style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.2em',
                  color: i === era.trajectory.length - 1 ? HQ.amber : HQ.text3,
                  textTransform: 'uppercase', marginBottom: 6,
                }}>
                  {t.season}
                </div>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{t.icon || '•'}</div>
                <div style={{ fontSize: 12, color: HQ.text, fontWeight: 600, marginBottom: 4, lineHeight: 1.2 }}>
                  {t.dominant_archetype}
                </div>
                <div style={{ fontSize: 10, color: HQ.text3, fontFamily: 'var(--font-mono)' }}>
                  {t.count}/{t.total_classified} · {Math.round(t.share * 100)}%
                </div>
              </Panel>
            ))}
          </div>
        </Section>
      )}

      <Section title="Rivalry Heat Index" sub="Heated and Personal-tier pairs only">
        {heat.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No rivalries above Active tier.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {heat.map((h, i) => (
              <Panel key={i} accent={h.tier === 'Personal' ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                  <strong style={{ color: HQ.text, fontSize: 14 }}>
                    {h.a_display_name} vs {h.b_display_name}
                  </strong>
                  <Tag color={h.tier === 'Personal' ? HQ.red : HQ.amber}>
                    {h.tier_icon} {h.tier} · {Math.round(h.score)}
                  </Tag>
                </div>
                <Narrative text={h.narrative} />
                {h.moments?.length > 0 && (
                  <ul style={{ marginTop: 8, paddingLeft: 18, color: HQ.text3, fontSize: 12, lineHeight: 1.6 }}>
                    {h.moments.slice(0, 2).map((m, j) => (
                      <li key={j}><span style={{ color: HQ.text2 }}>{m.headline}</span> · {m.detail}</li>
                    ))}
                  </ul>
                )}
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Franchise Reputations" sub="Career identity from 5 seasons of dynasty history">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
          {reputations.map((r) => (
            <Panel key={r.user_id} accent={idColor[r.identity_kind] || HQ.text3}>
              <div style={{ marginBottom: 4, color: HQ.text, fontWeight: 600, fontSize: 13 }}>
                {r.display_name}
              </div>
              <div style={{
                fontFamily: 'var(--font-display)',
                fontSize: '1rem',
                letterSpacing: '2px',
                color: idColor[r.identity_kind],
                textTransform: 'uppercase',
                marginBottom: 4,
              }}>{r.identity}</div>
              <div style={{ fontSize: 11, color: HQ.text3, marginBottom: 8 }}>{r.identity_blurb}</div>
              <div style={{ fontSize: 11, color: HQ.text2, marginBottom: 6 }}>{r.archetype}</div>
              {(r.career_titles > 0 || r.runner_ups > 0 || r.playoff_apps > 0) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {r.career_titles > 0 && (
                    <Tag color={HQ.green}>🏆 {r.career_titles} title{r.career_titles === 1 ? '' : 's'}</Tag>
                  )}
                  {r.runner_ups > 0 && (
                    <Tag color={HQ.amber}>🥈 {r.runner_ups} runner-up{r.runner_ups === 1 ? '' : 's'}</Tag>
                  )}
                  {r.playoff_apps > 0 && (
                    <Tag color={HQ.blue}>{r.playoff_apps} playoff app{r.playoff_apps === 1 ? '' : 's'}</Tag>
                  )}
                </div>
              )}
            </Panel>
          ))}
        </div>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// SYSTEM 5 — War Room
// ─────────────────────────────────────────────
function System5({ data }) {
  if (!data) return null;
  const pulse = data.league_pulse || {};
  const matrix = data.threat_matrix || [];
  const orphans = data.orphan_risk || [];
  const economy = data.economy || {};
  const warnings = data.early_warnings || [];
  const feed = data.intelligence_feed || [];

  const statusColor = {
    rising: HQ.green, peaking: HQ.blue, hoarding: HQ.purple,
    vulnerable: HQ.amber, collapsing: HQ.red, steady: HQ.text2,
    early: HQ.text3,
  };

  return (
    <>
      <Section title="League Pulse" sub="Daily briefing">
        <Panel accent={HQ.amber}>
          <Narrative text={pulse.headline} />
          {pulse.lines?.length > 1 && (
            <ul style={{ marginTop: 10, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {pulse.lines.slice(1).map((l, i) => (
                <li key={i} style={{ fontSize: 13, color: HQ.text2, lineHeight: 1.5, paddingLeft: 16, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: HQ.amber }}>▸</span>
                  <span dangerouslySetInnerHTML={{ __html: l.replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${HQ.text}">$1</strong>`) }} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </Section>

      <Section title="Early Warning System" sub="Predictive flags">
        {warnings.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No early-warning signals firing.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {warnings.map((w, i) => (
              <Panel key={i} accent={w.severity === 'high' ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                  <strong style={{ color: HQ.text, fontSize: 13 }}>{w.headline}</strong>
                  <Tag color={w.severity === 'high' ? HQ.red : HQ.amber}>{w.severity}</Tag>
                </div>
                <div style={{ fontSize: 12, color: HQ.text2, lineHeight: 1.5 }}>{w.body}</div>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="Dynasty Threat Matrix" sub="Current status layered with career achievements">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
          {matrix.map((m) => (
            <Panel key={m.user_id} accent={statusColor[m.status] || HQ.text3}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <strong style={{ color: HQ.text, fontSize: 13 }}>{m.display_name}</strong>
                <Tag color={statusColor[m.status]}>{m.threat_label}</Tag>
              </div>
              <div style={{ fontSize: 11, color: HQ.text3, fontFamily: 'var(--font-mono)' }}>
                {m.record} · CHAMP {Math.round(m.championship_odds * 100)}% · PO {Math.round(m.playoff_odds * 100)}%
              </div>
              <div style={{ fontSize: 11, color: HQ.text2, marginTop: 4 }}>
                {m.archetype}{m.drift && m.drift !== 'stable' ? ` · ${m.drift}` : ''}
              </div>
              {(m.career_tags?.length > 0) && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                  {m.career_tags.map((t) => (
                    <Tag key={t} color={
                      t === 'Dynasty' || t === 'Champion' ? HQ.green :
                      t === 'Tormentor' ? HQ.red :
                      t === 'Operator' ? HQ.purple :
                      t === 'Resilient' ? HQ.amber : HQ.blue
                    }>{t}</Tag>
                  ))}
                </div>
              )}
            </Panel>
          ))}
        </div>
      </Section>

      <Section title="Orphan Risk" sub="Disengagement + burnout prediction">
        {orphans.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No orphan signals.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {orphans.map((o) => (
              <Panel key={o.user_id} accent={o.risk_score >= 70 ? HQ.red : HQ.amber}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <strong style={{ color: HQ.text, fontSize: 13 }}>{o.display_name}</strong>
                  <Tag color={o.risk_score >= 70 ? HQ.red : HQ.amber}>{o.risk_score}/100</Tag>
                </div>
                <div style={{ fontSize: 12, color: HQ.text2 }}>
                  {o.predicted_outcome} · win rate {Math.round(o.win_rate * 100)}%
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <Section title="League Economy" sub={`Career across ${(economy.seasons_analyzed || []).length} seasons + this season's velocity`}>
        <Panel accent={HQ.blue}>
          <div style={{
            fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.2em',
            color: HQ.amber, textTransform: 'uppercase', marginBottom: 8,
          }}>This Season</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat label="Trades" value={economy.completed_trades} accent={HQ.green} />
            <Stat label="Picks Moved" value={economy.pick_movements} accent={HQ.blue} />
            <Stat label="Players Moved" value={economy.player_movements} accent={HQ.amber} />
            <Stat label="Waiver Adds" value={economy.waiver_acquisitions} accent={HQ.text2} />
            <Stat label="FAAB Spent" value={`$${economy.total_faab_spent}`} accent={HQ.warning} />
            <Stat label="Volume" value={economy.trade_volume_label} accent={HQ.amber} />
            <Stat label="Pick Liquidity" value={economy.pick_liquidity} accent={HQ.purple} />
          </div>
          <div style={{
            fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.2em',
            color: HQ.purple, textTransform: 'uppercase', marginBottom: 8,
          }}>All-Time ({(economy.seasons_analyzed || []).join(' · ')})</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <Stat label="Career Trades" value={economy.career_completed_trades} accent={HQ.green} big />
            <Stat label="Career Picks" value={economy.career_pick_movements} accent={HQ.blue} big />
            <Stat label="Career Players" value={economy.career_player_movements} accent={HQ.amber} />
            <Stat label="Career Waivers" value={economy.career_waiver_acquisitions} accent={HQ.text2} />
            <Stat label="Career FAAB" value={`$${economy.career_faab_spent}`} accent={HQ.warning} />
          </div>
          <Narrative text={economy.narrative} />
        </Panel>
      </Section>

      <Section title="Intelligence Feed" sub="Most recent transactions across the dynasty's full history">
        {feed.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No recent transactions.</div></Panel>
        ) : (
          <div style={{ background: HQ.panelDeep, border: `1px solid ${HQ.borderSoft}`, borderRadius: 8, maxHeight: 480, overflowY: 'auto' }}>
            {feed.map((f, i) => (
              <div key={i} style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${HQ.borderSoft}`,
                fontSize: 12,
                display: 'grid',
                gridTemplateColumns: '90px 1fr',
                gap: 10,
                alignItems: 'baseline',
              }}>
                <span style={{ color: HQ.text3, fontFamily: 'var(--font-mono)', fontSize: 10 }}>
                  {f.season ?? '—'} · W{f.leg ?? '—'}
                </span>
                <div>
                  <div style={{ color: HQ.text }}>{f.label}</div>
                  {f.detail && <div style={{ color: HQ.text3, fontSize: 11 }}>{f.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// LEAGUE SETTINGS PANEL
// Commissioner-only. Shows read view + editable fields.
// ─────────────────────────────────────────────
function SettingsPanel({ password }) {
  const [settings, setSettings] = useState(null);
  const [editable, setEditable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/hq/settings`, {
        headers: { 'X-HQ-Pass': password },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setSettings(data.settings);
      setEditable(data.editable_fields || []);
    } catch (e) {
      setMsg(`Failed to load: ${e.message}`);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleChange = (key, val) => setEdits(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    if (!Object.keys(edits).length) return;
    setSaving(true);
    setMsg('');
    try {
      const r = await fetch(`${API}/api/hq/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': password },
        body: JSON.stringify({ ...edits, performed_by: 'commissioner' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      setSettings(updated);
      setEdits({});
      setMsg('Settings saved.');
    } catch (e) {
      setMsg(`Save failed: ${e.message}`);
    }
    setSaving(false);
  };

  const renderField = (key, val) => {
    const isEditField = editable.includes(key);
    const currentVal = key in edits ? edits[key] : val;
    const labelStyle = { color: HQ.text2, fontSize: 11, fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.1em' };
    const valStyle = { color: HQ.text, fontSize: 13, fontFamily: 'var(--font-mono)' };
    const inputStyle = { background: HQ.panelDeep, border: `1px solid ${HQ.borderSoft}`, borderRadius: 6, color: HQ.text, fontFamily: 'var(--font-mono)', fontSize: 13, padding: '5px 10px', width: '100%' };

    let displayVal = val;
    if (Array.isArray(val)) displayVal = val.join(', ');
    else if (val !== null && typeof val === 'object') displayVal = JSON.stringify(val, null, 2);

    return (
      <div key={key} style={{ marginBottom: 16 }}>
        <div style={labelStyle}>{key.replace(/_/g, ' ')}</div>
        {isEditField ? (
          key === 'undrafted_keeper_policy' ? (
            <select
              value={currentVal}
              onChange={e => handleChange(key, e.target.value)}
              style={inputStyle}
            >
              <option value="final_round">Final-round cost</option>
              <option value="not_keepable">Not keepable</option>
            </select>
          ) : typeof val === 'boolean' ? (
            <select
              value={String(currentVal)}
              onChange={e => handleChange(key, e.target.value === 'true')}
              style={inputStyle}
            >
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          ) : typeof val === 'number' ? (
            <input
              type="number"
              step={key === 'ppr_setting' ? '0.25' : '1'}
              value={currentVal}
              onChange={e => handleChange(key, key === 'ppr_setting' ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
              style={inputStyle}
            />
          ) : (
            <input
              type="text"
              value={currentVal}
              onChange={e => handleChange(key, e.target.value)}
              style={inputStyle}
            />
          )
        ) : (
          <div style={valStyle}>
            {typeof displayVal === 'string' && displayVal.length > 80
              ? <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 11, color: HQ.text3 }}>{displayVal}</pre>
              : String(displayVal)}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Loading settings...</div></Panel>;

  const readOnly = settings ? Object.entries(settings).filter(([k]) => !editable.includes(k) && k !== 'id') : [];
  const editableFields = settings ? Object.entries(settings).filter(([k]) => editable.includes(k)) : [];

  return (
    <>
      <Section title="Editable League Settings" color={HQ.amber}>
        <Panel>
          {msg && (
            <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, background: msg.startsWith('Save') ? '#16a34a22' : '#ff475722', color: msg.startsWith('Save') ? HQ.green : HQ.red, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
              {msg}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 24px' }}>
            {editableFields.map(([k, v]) => renderField(k, v))}
          </div>
          {Object.keys(edits).length > 0 && (
            <button
              onClick={save}
              disabled={saving}
              style={{ ...btnStyle(HQ.green, saving), marginTop: 16 }}
            >
              {saving ? 'Saving...' : '✓ Save Changes'}
            </button>
          )}
        </Panel>
      </Section>

      <Section title="Read-Only Configuration" color={HQ.text3}>
        <Panel>
          <div style={{ fontSize: 11, color: HQ.text3, marginBottom: 12, fontFamily: 'var(--font-mono)' }}>
            These fields require a code-level change to modify.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '4px 24px' }}>
            {readOnly.map(([k, v]) => renderField(k, v))}
          </div>
        </Panel>
      </Section>
    </>
  );
}


// ─────────────────────────────────────────────
// FEATURE FLAGS PANEL
// Commissioner-only. Toggle grid with audit trail.
// ─────────────────────────────────────────────
function FlagsPanel({ password }) {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(new Set());
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/hq/flags`, { headers: { 'X-HQ-Pass': password } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setFlags(data.flags || []);
    } catch (e) {
      setMsg(`Failed to load: ${e.message}`);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (flagName, newEnabled) => {
    setToggling(prev => new Set([...prev, flagName]));
    setMsg('');
    try {
      const r = await fetch(`${API}/api/hq/flags/${flagName}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': password },
        body: JSON.stringify({ enabled: newEnabled, performed_by: 'commissioner' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      setFlags(prev => prev.map(f => f.flag_name === flagName ? { ...f, enabled: newEnabled, updated_at: new Date().toISOString(), updated_by: 'commissioner' } : f));
      setMsg(`${flagName.replace(/_/g, ' ')} ${newEnabled ? 'enabled' : 'disabled'}.`);
    } catch (e) {
      setMsg(`Toggle failed: ${e.message}`);
    }
    setToggling(prev => { const s = new Set(prev); s.delete(flagName); return s; });
  };

  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Loading flags...</div></Panel>;

  const enabled = flags.filter(f => f.enabled);
  const disabled = flags.filter(f => !f.enabled);

  return (
    <>
      <Section title={`Feature Flags — ${enabled.length} enabled · ${disabled.length} disabled`} color={HQ.amber}>
        {msg && (
          <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: msg.includes('failed') ? '#ff475722' : '#16a34a22', color: msg.includes('failed') ? HQ.red : HQ.green, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            {msg}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 8 }}>
          {flags.map(flag => {
            const isToggling = toggling.has(flag.flag_name);
            const accentColor = flag.enabled ? HQ.green : HQ.text3;
            const lastChanged = flag.updated_at
              ? new Date(flag.updated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : 'default';
            return (
              <div key={flag.flag_name} style={{
                background: HQ.panelDeep,
                border: `1px solid ${flag.enabled ? HQ.green + '30' : HQ.borderSoft}`,
                borderRadius: 8,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: flag.enabled ? HQ.text : HQ.text2, letterSpacing: '0.05em' }}>
                    {flag.flag_name.replace(/_/g, ' ').toUpperCase()}
                  </div>
                  <div style={{ fontSize: 11, color: HQ.text3, marginTop: 2 }}>
                    {flag.description || ''}
                  </div>
                  <div style={{ fontSize: 10, color: HQ.text3, marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                    Last changed: {lastChanged}
                    {flag.updated_by && flag.updated_by !== 'default' ? ` · by ${flag.updated_by}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => toggle(flag.flag_name, !flag.enabled)}
                  disabled={isToggling}
                  style={{
                    background: flag.enabled ? HQ.green + '22' : 'transparent',
                    border: `1px solid ${accentColor}55`,
                    color: accentColor,
                    padding: '5px 12px',
                    borderRadius: 6,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    cursor: isToggling ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {isToggling ? '...' : flag.enabled ? 'ON' : 'OFF'}
                </button>
              </div>
            );
          })}
        </div>
      </Section>
    </>
  );
}


// ─────────────────────────────────────────────
// Main HQ
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// DRAFT CLASS PANEL — Phase 4 commissioner editor
// ─────────────────────────────────────────────
const DRAFT_STRENGTHS = ['Elite', 'Strong', 'Average', 'Weak', 'Poor', 'Unknown'];
const DRAFT_CONFIDENCE = ['High', 'Medium', 'Low', 'Unknown'];
const ACTIVE_SEASON = 2026;
const DRAFT_YEARS = [ACTIVE_SEASON, ACTIVE_SEASON + 1, ACTIVE_SEASON + 2];

function DraftClassPanel({ password }) {
  const [profiles, setProfiles] = useState({});
  const [activeYear, setActiveYear] = useState(ACTIVE_SEASON);
  const [form, setForm] = useState({ overall_strength: 'Unknown', confidence_level: 'Unknown', notes: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadProfile = async (year) => {
    try {
      const r = await fetch(`${API}/api/hq/draft-class/${year}`, { headers: { 'X-HQ-Pass': password } });
      if (r.ok) {
        const d = await r.json();
        setProfiles(prev => ({ ...prev, [year]: d }));
        if (year === activeYear) {
          setForm({ overall_strength: d.overall_strength || 'Unknown', confidence_level: d.confidence_level || 'Unknown', notes: d.notes || '' });
        }
      }
    } catch (e) { console.error('[DraftClass] load failed', e); }
  };

  useEffect(() => { DRAFT_YEARS.forEach(loadProfile); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const p = profiles[activeYear];
    if (p) setForm({ overall_strength: p.overall_strength || 'Unknown', confidence_level: p.confidence_level || 'Unknown', notes: p.notes || '' });
    else setForm({ overall_strength: 'Unknown', confidence_level: 'Unknown', notes: '' });
  }, [activeYear, profiles]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await fetch(`${API}/api/hq/draft-class/${activeYear}`, {
        method: 'POST',
        headers: { 'X-HQ-Pass': password, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, performed_by: 'commissioner' }),
      });
      const d = await r.json();
      if (r.ok) { setProfiles(prev => ({ ...prev, [activeYear]: d })); setMsg('Saved. Draft class profile updated and audit logged.'); }
      else setMsg(`Error: ${d.detail || 'Save failed'}`);
    } catch (e) { setMsg(`Save failed: ${e.message}`); }
    setSaving(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const STRENGTH_COLORS = { Elite: HQ.green, Strong: '#4ade80', Average: HQ.amber, Weak: '#fb923c', Poor: HQ.red, Unknown: HQ.text3 };

  return (
    <div>
      <Section title="Draft Class Profile Editor" sub="Set draft class strength — affects all pick market valuations">
        <div style={{ fontSize: 12, color: HQ.text3, marginBottom: 16, lineHeight: 1.6 }}>
          Each draft year has a class profile that adjusts every pick's market value. An Elite class rating adds 15% to all picks for that year. Unknown leaves a 5% discount until you set it.
        </div>

        {/* Year selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {DRAFT_YEARS.map(yr => {
            const p = profiles[yr];
            const color = STRENGTH_COLORS[p?.overall_strength || 'Unknown'] || HQ.text3;
            return (
              <button key={yr} onClick={() => setActiveYear(yr)} style={{
                background: activeYear === yr ? `${color}18` : HQ.panelDeep,
                border: `1px solid ${activeYear === yr ? color : HQ.borderSoft}`,
                borderRadius: 8, color: activeYear === yr ? color : HQ.text2,
                padding: '8px 18px', fontFamily: 'var(--font-mono)', fontSize: 13,
                fontWeight: 700, cursor: 'pointer',
              }}>
                {yr}
                {p && p.overall_strength !== 'Unknown' && (
                  <span style={{ fontSize: 9, marginLeft: 6, color }}>{p.overall_strength}</span>
                )}
              </button>
            );
          })}
        </div>

        <Panel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.2em', color: HQ.text3, textTransform: 'uppercase', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Overall Strength</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DRAFT_STRENGTHS.map(s => {
                  const c = STRENGTH_COLORS[s] || HQ.text3;
                  return (
                    <button key={s} onClick={() => setForm(f => ({ ...f, overall_strength: s }))} style={{
                      background: form.overall_strength === s ? `${c}22` : HQ.panelDeep,
                      border: `1px solid ${form.overall_strength === s ? c : HQ.borderSoft}`,
                      color: form.overall_strength === s ? c : HQ.text3,
                      padding: '4px 10px', borderRadius: 5,
                      fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer',
                    }}>{s}</button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.2em', color: HQ.text3, textTransform: 'uppercase', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Commissioner Confidence</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DRAFT_CONFIDENCE.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, confidence_level: c }))} style={{
                    background: form.confidence_level === c ? `${HQ.amber}22` : HQ.panelDeep,
                    border: `1px solid ${form.confidence_level === c ? HQ.amber : HQ.borderSoft}`,
                    color: form.confidence_level === c ? HQ.amber : HQ.text3,
                    padding: '4px 10px', borderRadius: 5,
                    fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer',
                  }}>{c}</button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.2em', color: HQ.text3, textTransform: 'uppercase', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Notes</div>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Class notes, position-specific details, analyst consensus..."
              style={{ width: '100%', background: HQ.panelDeep, border: `1px solid ${HQ.borderSoft}`, borderRadius: 6, color: HQ.text, padding: '10px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={save} disabled={saving} style={{
              background: saving ? HQ.text3 : HQ.amber, color: '#0a0a0a',
              border: 'none', borderRadius: 6, padding: '8px 20px',
              fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', cursor: saving ? 'wait' : 'pointer',
            }}>
              {saving ? 'Saving...' : `Save ${activeYear} Profile`}
            </button>
            {msg && <span style={{ fontSize: 12, color: msg.startsWith('Error') ? HQ.red : HQ.green, fontFamily: 'var(--font-mono)' }}>{msg}</span>}
          </div>
        </Panel>

        {/* Modifier reference */}
        <div style={{ marginTop: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['Elite', '+15%', HQ.green], ['Strong', '+8%', '#4ade80'], ['Average', '0%', HQ.amber], ['Weak', '-8%', '#fb923c'], ['Poor', '-15%', HQ.red], ['Unknown', '-5%', HQ.text3]].map(([s, pct, c]) => (
            <div key={s} style={{ padding: '4px 10px', borderRadius: 5, background: `${c}12`, border: `1px solid ${c}22`, fontSize: 10, color: c, fontFamily: 'var(--font-mono)' }}>
              {s}: {pct}
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─────────────────────────────────────────────
// USAGE PANEL — Platform usage monitoring
// Commissioner-only. Never exposed in public UI.
// ─────────────────────────────────────────────
function UsagePanel({ password }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/api/commissioner/usage`, {
          headers: { 'X-HQ-Pass': password },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setData(await r.json());
      } catch (e) {
        setError(e.message);
      }
      setLoading(false);
    };
    load(); /* eslint-disable-next-line */
  }, []);

  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Loading usage data...</div></Panel>;
  if (error) return <Panel accent={HQ.red}><div style={{ color: HQ.red, fontSize: 13 }}>Failed to load: {error}</div></Panel>;
  if (!data) return null;

  const daily = (data.daily_active || []).slice(0, 14);
  const topEndpoints = (data.top_endpoints || []).slice(0, 5);
  const lastSeen = data.last_seen || [];
  const funnel = data.feature_funnel || {};
  const dailyBreakdown = data.daily_breakdown || [];
  const mostActive = data.most_active_rosters || [];

  const thStyle = {
    textAlign: 'left',
    padding: '6px 10px',
    color: HQ.text3,
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${HQ.borderSoft}`,
  };
  const tdStyle = (align) => ({
    padding: '7px 10px',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    textAlign: align || 'left',
  });

  return (
    <>
      <Section title="Feature Funnel" sub="All-time usage counts">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Stat label="Mock Draft Started"    value={funnel.mock_draft_started ?? 0}   accent={HQ.blue}   big />
          <Stat label="Mock Draft Completed"  value={funnel.mock_draft_completed ?? 0} accent={HQ.green}  big />
          <Stat label="Trade Lab Used"        value={funnel.trade_lab_used ?? 0}       accent={HQ.amber}  big />
          <Stat label="Keeper Tool Used"      value={funnel.keeper_tool_used ?? 0}     accent={HQ.purple} big />
        </div>
      </Section>

      <Section title="Daily Activity" sub="Last 14 days">
        <Panel>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Requests</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Unique Rosters</th>
              </tr>
            </thead>
            <tbody>
              {daily.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: '12px 10px', color: HQ.text3, textAlign: 'center', fontSize: 13 }}>
                    No activity logged yet.
                  </td>
                </tr>
              ) : daily.map((row, i) => (
                <tr key={row.date} style={{ background: i % 2 === 0 ? 'transparent' : `${HQ.borderSoft}18` }}>
                  <td style={{ ...tdStyle(), color: HQ.text2 }}>{row.date}</td>
                  <td style={{ ...tdStyle(), color: HQ.text }}>{row.requests}</td>
                  <td style={{ ...tdStyle('right'), color: HQ.text3 }}>{row.unique_rosters}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </Section>

      <Section title="Top Endpoints" sub="5 most-used API paths">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {topEndpoints.length === 0 ? (
            <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No endpoint data yet.</div></Panel>
          ) : topEndpoints.map((ep, i) => (
            <div key={ep.endpoint} style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr 70px',
              gap: 10,
              alignItems: 'center',
              padding: '8px 12px',
              background: HQ.panel,
              border: `1px solid ${HQ.borderSoft}`,
              borderRadius: 6,
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: HQ.text3 }}>#{i + 1}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: HQ.text2, wordBreak: 'break-all' }}>{ep.endpoint}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: HQ.amber, textAlign: 'right' }}>{ep.count}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Last Seen" sub="Most recent activity per roster">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {lastSeen.length === 0 ? (
            <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No roster activity yet.</div></Panel>
          ) : lastSeen.map((entry) => (
            <div key={entry.roster_id} style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'center',
              padding: '8px 12px',
              background: HQ.panel,
              border: `1px solid ${HQ.borderSoft}`,
              borderRadius: 6,
            }}>
              <span style={{ color: HQ.text, fontSize: 13, fontWeight: 600 }}>
                {entry.display_name || `Roster ${entry.roster_id}`}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: HQ.text3, whiteSpace: 'nowrap' }}>
                {new Date(entry.last_active).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Most Active Managers" sub="All-time request count, ranked">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {mostActive.length === 0 ? (
            <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No activity data yet.</div></Panel>
          ) : mostActive.map((entry, i) => (
            <div key={entry.roster_id} style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr 80px 140px',
              gap: 10,
              alignItems: 'center',
              padding: '8px 12px',
              background: HQ.panel,
              border: `1px solid ${HQ.borderSoft}`,
              borderLeft: i === 0 ? `3px solid ${HQ.amber}` : `1px solid ${HQ.borderSoft}`,
              borderRadius: 6,
            }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: i === 0 ? HQ.amber : HQ.text3 }}>
                #{i + 1}
              </span>
              <span style={{ color: HQ.text, fontSize: 13, fontWeight: 600 }}>
                {entry.display_name}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: HQ.amber, textAlign: 'right' }}>
                {entry.total_requests.toLocaleString()}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: HQ.text3, textAlign: 'right' }}>
                {new Date(entry.last_active).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Daily Breakdown" sub="Per-manager activity for the last 30 days">
        <Panel>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total Requests</th>
              </tr>
            </thead>
            <tbody>
              {dailyBreakdown.length === 0 ? (
                <tr>
                  <td colSpan={2} style={{ padding: '12px 10px', color: HQ.text3, textAlign: 'center', fontSize: 13 }}>
                    No activity logged yet.
                  </td>
                </tr>
              ) : dailyBreakdown.map((row, i) => (
                <React.Fragment key={row.date}>
                  <tr style={{ background: i % 2 === 0 ? 'transparent' : `${HQ.borderSoft}18` }}>
                    <td style={{ ...tdStyle(), color: HQ.text2, fontWeight: 600 }}>{row.date}</td>
                    <td style={{ ...tdStyle('right'), color: HQ.text }}>{row.total_requests}</td>
                  </tr>
                  {row.by_roster.length > 0 && (
                    <tr style={{ background: i % 2 === 0 ? 'transparent' : `${HQ.borderSoft}18` }}>
                      <td colSpan={2} style={{ padding: '0 10px 8px 24px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                          {row.by_roster.map((m) => (
                            <span key={m.roster_id} style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: 10,
                              color: HQ.text3,
                            }}>
                              {m.display_name} <span style={{ color: HQ.text2 }}>{m.requests}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Panel>
      </Section>
    </>
  );
}

// ─────────────────────────────────────────────
// SITE OPS AGENT — flow, reliability and maintenance intelligence
// ─────────────────────────────────────────────
function SiteOpsPanel({ password }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API}/api/commissioner/site-ops`, { headers: { 'X-HQ-Pass': password } });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || `HTTP ${response.status}`);
      setData(payload);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const autopilotAction = async (path, options = {}) => {
    setActing(true); setError('');
    try {
      const response = await fetch(`${API}${path}`, {
        method: options.method || 'POST',
        headers: { 'X-HQ-Pass': password, 'Content-Type': 'application/json' },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || `HTTP ${response.status}`);
      await load();
    } catch (err) { setError(err.message); }
    setActing(false);
  };
  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Site Ops is reconciling flow and reliability signals…</div></Panel>;
  if (error) return <Panel accent={HQ.red}><div style={{ color: HQ.red, fontSize: 13 }}>Site Ops failed to load: {error}</div></Panel>;
  if (!data) return null;
  const summary = data.summary || {};
  const statusColor = data.status === 'CRITICAL' ? HQ.red : data.status === 'WATCH' ? HQ.warning : HQ.green;
  const priorityColor = priority => priority === 'P0' ? HQ.red : priority === 'P1' ? HQ.warning : priority === 'P2' ? HQ.blue : HQ.green;
  return <>
    <Section title="Site Operations Agent" sub={`Live diagnosis · ${new Date(data.generated_at).toLocaleString()}`}>
      <Panel accent={statusColor}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div><div style={{ color: HQ.text3, font: '10px var(--font-mono)', letterSpacing: '.14em' }}>CURRENT SITE STATE</div><div style={{ marginTop: 5, color: statusColor, font: '400 1.8rem var(--font-display)' }}>{data.status}</div></div>
          <button onClick={load} className="hq-tab-btn">↻ Re-run diagnosis</button>
        </div>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 14 }}>
          <Stat label="Sessions · 7d" value={summary.sessions || 0} accent={HQ.blue} />
          <Stat label="Page Views" value={summary.page_views || 0} accent={HQ.amber} />
          <Stat label="Clicks" value={summary.clicks || 0} accent={HQ.purple} />
          <Stat label="Client Errors" value={summary.errors || 0} accent={(summary.errors || 0) ? HQ.red : HQ.green} />
        </div>
      </Panel>
    </Section>

    <Section title="Guarded Autopilot" sub="Runs every 15 minutes · reversible operational repairs only">
      <Panel accent={data.autopilot?.enabled ? HQ.green : HQ.warning}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div><Tag color={data.autopilot?.enabled ? HQ.green : HQ.warning}>{data.autopilot?.enabled ? 'ACTIVE' : 'PAUSED'}</Tag><p style={{ margin: '9px 0 0', color: HQ.text2, fontSize: 12, lineHeight: 1.55, maxWidth: 720 }}>{data.autopilot?.policy}</p><small style={{ display: 'block', marginTop: 8, color: HQ.text3 }}>Last run: {data.autopilot?.last_run_at ? new Date(data.autopilot.last_run_at).toLocaleString() : 'waiting for first cycle'}{data.autopilot?.last_result ? ` · ${data.autopilot.last_result}` : ''}</small></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button disabled={acting} onClick={() => autopilotAction('/api/commissioner/site-ops/autopilot/run')} className="hq-tab-btn">{acting ? 'Working…' : 'Run safe repairs now'}</button>
            <button disabled={acting} onClick={() => autopilotAction('/api/commissioner/site-ops/autopilot', { method: 'PATCH', body: { enabled: !data.autopilot?.enabled } })} className="hq-tab-btn">{data.autopilot?.enabled ? 'Pause' : 'Resume'}</button>
          </div>
        </div>
      </Panel>
      {(data.autopilot?.actions || []).length > 0 && <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
        {data.autopilot.actions.slice(0, 8).map(action => <Panel key={action.id} accent={action.status === 'fixed' ? HQ.green : HQ.blue}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><strong style={{ color: HQ.text, fontSize: 12 }}>{action.detail}</strong><Tag color={action.status === 'fixed' ? HQ.green : HQ.blue}>{action.status}</Tag></div>
          <small style={{ display: 'block', marginTop: 7, color: HQ.text3 }}>{new Date(action.created_at).toLocaleString()} · {action.trigger}{action.rows_affected ? ` · ${action.rows_affected} records repaired` : ''}</small>
        </Panel>)}
      </div>}
    </Section>

    <Section title="Maintenance Queue" sub="Evidence-ranked areas of improvement">
      <div style={{ display: 'grid', gap: 8 }}>
        {(data.recommendations || []).map((item, index) => <Panel key={`${item.title}-${index}`} accent={priorityColor(item.priority)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}><strong style={{ color: HQ.text, fontSize: 13 }}>{item.title}</strong><Tag color={priorityColor(item.priority)}>{item.priority} · {item.category}</Tag></div>
          <p style={{ margin: '8px 0 4px', color: HQ.text2, fontSize: 12, lineHeight: 1.5 }}>{item.evidence}</p>
          <p style={{ margin: 0, color: HQ.text3, fontSize: 11, lineHeight: 1.5 }}><b style={{ color: priorityColor(item.priority) }}>NEXT:</b> {item.action}</p>
        </Panel>)}
      </div>
    </Section>

    <Section title="User Flow" sub="Most visited routes and interaction depth · 7 days">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 8 }}>
        {(data.pages || []).slice(0, 12).map(row => <Panel key={`${row.league}-${row.page}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><strong style={{ color: HQ.text, font: '11px var(--font-mono)', wordBreak: 'break-all' }}>{row.page}</strong><Tag color={row.league === 'lor' ? HQ.amber : HQ.blue}>{row.league}</Tag></div>
          <div style={{ marginTop: 8, color: HQ.text3, font: '10px var(--font-mono)' }}>{row.views} views · {row.sessions} sessions · {row.clicks} clicks</div>
        </Panel>)}
        {!(data.pages || []).length && <Panel><div style={{ color: HQ.text3, fontSize: 12 }}>Client flow collection has just started. Insights will populate as members browse.</div></Panel>}
      </div>
    </Section>

    <Section title="API Health" sub="Slow and failing service paths · 7 days">
      <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}><thead><tr>{['Endpoint','Calls','Average','P95','Failures'].map(label => <th key={label} style={{ textAlign: 'left', padding: 8, color: HQ.text3, borderBottom: `1px solid ${HQ.borderSoft}`, fontFamily: 'var(--font-mono)' }}>{label}</th>)}</tr></thead><tbody>
        {(data.api_health || []).slice(0, 15).map(row => <tr key={row.endpoint}><td style={{ padding: 8, color: HQ.text2, fontFamily: 'var(--font-mono)' }}>{row.endpoint}</td><td style={{ padding: 8 }}>{row.count}</td><td style={{ padding: 8 }}>{row.avg_ms} ms</td><td style={{ padding: 8, color: row.p95_ms >= 2500 ? HQ.warning : HQ.text2 }}>{row.p95_ms} ms</td><td style={{ padding: 8, color: row.failures ? HQ.red : HQ.green }}>{row.failures}</td></tr>)}
      </tbody></table></div>
    </Section>

    <Section title="Privacy Boundary" sub="What the IT agent deliberately cannot see"><Panel accent={HQ.green}><p style={{ margin: 0, color: HQ.text2, fontSize: 12, lineHeight: 1.55 }}>{data.privacy}</p></Panel></Section>
  </>;
}

export function MemberLoginsPanel({ password, onUnauthorized }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState({});
  const [messages, setMessages] = useState({});
  const [links, setLinks] = useState({});

  const unauthorized = () => {
    sessionStorage.removeItem(SS_KEY);
    onUnauthorized?.();
  };

  const loadMembers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/api/auth/commissioner/members`, {
        headers: { 'X-HQ-Pass': password },
      });
      if (response.status === 401) {
        unauthorized();
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMembers(data.members || []);
    } catch (err) {
      setError(`Unable to load member logins: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); /* eslint-disable-next-line */ }, []);

  const issueInvite = async (member, deliveryMode) => {
    const membershipId = member.memberships?.[0]?.membership_id;
    if (!membershipId) return;
    const key = `${member.display_name}:${deliveryMode}`;
    setWorking(prev => ({ ...prev, [key]: true }));
    setMessages(prev => ({ ...prev, [member.display_name]: '' }));
    if (deliveryMode === 'link') {
      setLinks(prev => ({ ...prev, [member.display_name]: '' }));
    }
    try {
      const response = await fetch(`${API}/api/auth/commissioner/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-HQ-Pass': password },
        body: JSON.stringify({ membership_id: membershipId, delivery_mode: deliveryMode }),
      });
      if (response.status === 401) {
        unauthorized();
        return;
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
      if (deliveryMode === 'link') {
        if (!data.redeem_url) throw new Error('Invite URL missing from response');
        setLinks(prev => ({ ...prev, [member.display_name]: data.redeem_url }));
        setMessages(prev => ({ ...prev, [member.display_name]: 'Link ready' }));
      } else {
        setMessages(prev => ({ ...prev, [member.display_name]: 'Sent ✓' }));
      }
    } catch (err) {
      setMessages(prev => ({ ...prev, [member.display_name]: `Error: ${err.message}` }));
    } finally {
      setWorking(prev => ({ ...prev, [key]: false }));
    }
  };

  const copyLink = async (member) => {
    const url = links[member.display_name];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setMessages(prev => ({ ...prev, [member.display_name]: 'Copied ✓' }));
    } catch {
      setMessages(prev => ({ ...prev, [member.display_name]: 'Error: copy failed — select the URL manually' }));
    }
  };

  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Loading member logins...</div></Panel>;

  return (
    <Section title="Member Logins" sub="Magic-link onboarding · session status">
      {error && <div className="hq-member-error">{error}</div>}
      <Panel style={{ padding: 0, overflow: 'hidden' }}>
        <div className="hq-member-table-wrap">
          <table className="hq-member-table">
            <thead>
              <tr><th>Member</th><th>League / role</th><th>Status</th><th>Invite actions</th></tr>
            </thead>
            <tbody>
              {members.map(member => {
                const discordKey = `${member.display_name}:discord`;
                const linkKey = `${member.display_name}:link`;
                const message = messages[member.display_name] || '';
                const manualUrl = links[member.display_name];
                return (
                  <tr key={member.display_name}>
                    <td className="hq-member-name">{member.display_name}</td>
                    <td>
                      <div className="hq-member-leagues">
                        {member.memberships.map(membership => (
                          <span key={membership.membership_id}>
                            {membership.league_name} · {membership.role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`hq-login-status ${member.active_session ? 'active' : 'inactive'}`}>
                        {member.active_session ? 'Logged in' : 'No session yet'}
                      </span>
                    </td>
                    <td>
                      <div className="hq-member-actions">
                        {member.has_discord && (
                          <button
                            type="button"
                            disabled={working[discordKey]}
                            onClick={() => issueInvite(member, 'discord')}
                            className="hq-member-action"
                          >
                            {working[discordKey] ? 'Sending...' : 'Send Discord link'}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={working[linkKey]}
                          onClick={() => issueInvite(member, 'link')}
                          className="hq-member-action"
                        >
                          {working[linkKey] ? 'Creating...' : 'Copy link'}
                        </button>
                      </div>
                      {message && (
                        <div className={`hq-member-message${message.startsWith('Error:') ? ' error' : ''}`}>
                          {message}
                        </div>
                      )}
                      {manualUrl && (
                        <div className="hq-manual-link">
                          <div className="hq-manual-link-row">
                            <input aria-label={`${member.display_name} invite URL`} readOnly value={manualUrl} />
                            <button type="button" onClick={() => copyLink(member)}>Copy</button>
                          </div>
                          <div>single-use · expires in ~30 min · send via Sleeper/DM.</div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!error && members.length === 0 && <div className="hq-member-empty">No seeded members found.</div>}
      </Panel>
    </Section>
  );
}

const TABS = [
  { key: 's1',       label: 'Health',        long: 'League Health & Stability' },
  { key: 's2',       label: 'Behavioral',    long: 'Behavioral Intelligence' },
  { key: 's3',       label: 'Integrity',     long: 'Governance & Integrity' },
  { key: 's4',       label: 'Narrative',     long: 'Narrative & Culture' },
  { key: 's5',       label: 'War Room',      long: 'Commissioner War Room' },
  { key: 'hof',      label: 'Legacy HOF',    long: 'Hall of Fame Management' },
  { key: 'settings', label: 'Settings',      long: 'League Settings Registry' },
  { key: 'flags',    label: 'Flags',         long: 'Feature Flag Control' },
  { key: 'draftclass', label: 'Draft Classes', long: 'Draft Class Profile Editor' },
  { key: 'usage',    label: 'Usage',         long: 'Platform Usage' },
  { key: 'siteops',  label: 'IT Agent',      long: 'Site Operations Agent' },
  { key: 'logins',   label: 'Member Logins', long: 'Magic-Link Onboarding' },
  { key: 'guide',    label: 'Master Guide',  long: 'Commissioner Master Guide' },
];

// ─────────────────────────────────────────────
// HOF PANEL
// Commissioner-only. Never exposed in public UI or patch notes.
// ─────────────────────────────────────────────
function HofPanel({ password }) {
  const [hofData, setHofData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ballots, setBallots] = useState({});
  const [ballotBusy, setBallotBusy] = useState('');

  const fetchPanel = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/commissioner/hof-panel`, {
        headers: { 'X-HQ-Pass': password },
      });
      if (r.ok) setHofData(await r.json());
    } catch (e) {
      console.error('[HofPanel] fetch failed', e);
    }
    setLoading(false);
  };

  const fetchBallots = async () => {
    const entries = await Promise.all(['dynasty', 'lor'].map(async league => {
      try {
        const r = await fetch(`${API}/api/hall-of-fame/${league}/ballot`, { credentials: 'include' });
        return [league, r.ok ? await r.json() : null];
      } catch { return [league, null]; }
    }));
    setBallots(Object.fromEntries(entries));
  };

  useEffect(() => { fetchPanel(); fetchBallots(); /* eslint-disable-next-line */ }, []);

  const changeBallot = async (league, action) => {
    setBallotBusy(`${league}:${action}`);
    try {
      const r = await fetch(`${API}/api/commissioner/hof-ballot/${action}`, {
        method: 'POST',
        headers: { 'X-HQ-Pass': password, 'Content-Type': 'application/json' },
        body: JSON.stringify({ league }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Ballot action failed');
      await Promise.all([fetchBallots(), fetchPanel()]);
    } catch (error) {
      window.alert(error.message);
    }
    setBallotBusy('');
  };

  const stateColor = { inducted: HQ.warning, eligible: HQ.green, watchlist: HQ.blue };
  const stateLabel = { inducted: '🏛️ Inducted', eligible: '⭐ HOF Eligible', watchlist: '👀 HOF Watchlist' };

  if (loading) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Loading HOF panel...</div></Panel>;
  if (!hofData) return <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>HOF panel unavailable.</div></Panel>;

  const panel = hofData.panel || [];

  return (
    <>
      <Section title="Hall of Fame Panel" sub="Watchlist · Eligible · Inducted">
        <Panel accent={HQ.warning}>
          <div style={{ color: HQ.text, fontWeight: 700, marginBottom: 5 }}>Annual League Ballots</div>
          <div style={{ color: HQ.text3, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            Open only after the championship, season audit and seven-day correction window. Every induction requires a vote; each league may induct at most one manager per class.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 8 }}>
            {['dynasty', 'lor'].map(league => {
              const ballot = ballots[league];
              const open = ballot?.status === 'open';
              return (
                <div key={league} style={{ border: `1px solid ${HQ.border}`, borderRadius: 7, padding: 11 }}>
                  <div style={{ color: HQ.text, fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>{league}</div>
                  <div style={{ color: open ? HQ.green : HQ.text3, fontSize: 11, margin: '4px 0 9px' }}>
                    {open ? `Class of ${ballot.class_year} · Voting open` : ballot?.status === 'closed' ? `Class of ${ballot.class_year} · Closed` : 'Not opened'}
                  </div>
                  <button onClick={() => changeBallot(league, open ? 'close' : 'open')} disabled={Boolean(ballotBusy)} style={{
                    background: open ? HQ.warning : HQ.green, color: '#080808', border: 0, borderRadius: 5,
                    padding: '6px 10px', cursor: ballotBusy ? 'wait' : 'pointer', fontSize: 10,
                    fontFamily: 'var(--font-mono)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em'
                  }}>{ballotBusy === `${league}:${open ? 'close' : 'open'}` ? 'Working…' : open ? 'Close & Certify' : 'Open Ballot'}</button>
                </div>
              );
            })}
          </div>
        </Panel>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Stat label="Inducted"  value={hofData.inducted_count}  accent={HQ.warning} />
          <Stat label="Eligible"  value={hofData.eligible_count}  accent={HQ.green} />
          <Stat label="Watchlist" value={hofData.watchlist_count} accent={HQ.blue} />
        </div>

        {panel.length === 0 ? (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>No franchises on the HOF radar yet.</div></Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {panel.map((entry) => (
              <Panel key={entry.franchise_id} accent={stateColor[entry.hof_state] || HQ.text3}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ color: HQ.text, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                      {entry.display_name}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Tag color={stateColor[entry.hof_state]}>
                        {stateLabel[entry.hof_state] || entry.hof_state}
                      </Tag>
                      <Tag color={HQ.text3}>{entry.highest_tier}</Tag>
                    </div>
                  </div>
                  {entry.hof_state !== 'inducted' && <Tag color={HQ.text3}>Ballot required</Tag>}
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

function HQDashboard({ password, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [active, setActive] = useState('s5');

  const load = async (force = false) => {
    setBusy(true);
    setError(null);
    try {
      const url = `${API}/api/hq/dashboard${force ? '?refresh=true' : ''}`;
      const r = await fetch(url, { headers: { 'X-HQ-Pass': password } });
      if (r.status === 401) {
        sessionStorage.removeItem(SS_KEY);
        onLogout();
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  const sysData = useMemo(() => ({
    s1: data?.system1, s2: data?.system2, s3: data?.system3,
    s4: data?.system4, s5: data?.system5,
  }), [data]);

  return (
    <div className="hq-shell">
      <div className="hq-inner">
        <div className="hq-header">
          <div>
            <div className="hq-eyebrow">Restricted · Internal Briefing</div>
            <h1 className="hq-title">Commissioner HQ</h1>
            {data && (
              <div className="hq-meta">
                {data.league_name} · WK {data.current_week} · Last refresh {new Date(data.calculated_at).toLocaleTimeString()}
              </div>
            )}
          </div>
          <div className="hq-btn-row">
            <button onClick={() => load(true)} disabled={busy} style={btnStyle(HQ.amber, busy)}>
              {busy ? '...' : '↻ Refresh'}
            </button>
            <button onClick={onLogout} style={btnStyle(HQ.text3, false)}>
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="hq-tab-bar">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              className={`hq-tab-btn${active === t.key ? ' active' : ''}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <Panel accent={HQ.red}>
            <div style={{ color: HQ.red, fontSize: 13 }}>Failed to load HQ payload: {error}</div>
          </Panel>
        )}
        {busy && !data && (
          <Panel><div style={{ color: HQ.text3, fontSize: 13 }}>Aggregating intelligence across 5 seasons...</div></Panel>
        )}

        {data && active === 's1' && <System1 data={sysData.s1} />}
        {data && active === 's2' && <System2 data={sysData.s2} />}
        {data && active === 's3' && <System3 data={sysData.s3} />}
        {data && active === 's4' && <System4 data={sysData.s4} />}
        {data && active === 's5' && <System5 data={sysData.s5} />}
        {active === 'hof'        && <HofPanel password={password} />}
        {active === 'settings'   && <SettingsPanel password={password} />}
        {active === 'flags'      && <FlagsPanel password={password} />}
        {active === 'draftclass' && <DraftClassPanel password={password} />}
        {active === 'usage'      && <UsagePanel password={password} />}
        {active === 'siteops'    && <SiteOpsPanel password={password} />}
        {active === 'logins'     && <MemberLoginsPanel password={password} onUnauthorized={onLogout} />}
        {active === 'guide'      && <CommissionerMasterGuide password={password} />}
      </div>
    </div>
  );
}

const btnStyle = (color, busy) => ({
  background: 'transparent',
  border: `1px solid ${color}55`,
  color: color,
  padding: '6px 14px',
  borderRadius: 6,
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: busy ? 'wait' : 'pointer',
  fontWeight: 600,
});

// ─────────────────────────────────────────────
// Page wrapper
// Mounted at /intel — App.js gates the nav link with isCommissioner so
// only the league commissioner ever sees the link. Password gate is the
// second factor here — entering the configured COMMISSIONER_PASSWORD
// loads the war room.
// ─────────────────────────────────────────────
export default function CommissionerHQ() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(SS_KEY) || null);

  if (!pw) return <PasswordGate onAuthenticated={setPw} />;
  return <HQDashboard password={pw} onLogout={() => { sessionStorage.removeItem(SS_KEY); setPw(null); }} />;
}
